import React, { useEffect, useState } from 'react';

import { Button } from '@/design-system/button';
import { FormModal } from '@/design-system/form-modal';
import { Textarea } from '@/design-system/textarea';
import { toast } from '@/design-system/toaster';
import {
  DISPUTE_REASON_LABELS,
  DisputeReason,
  Invoice,
  invoicesAPI,
} from '@/services/api/invoices';

interface DisputeModalProps {
  isOpen: boolean;
  invoice: Invoice | null;
  onClose: () => void;
  onUpdated: (invoice: Invoice) => void;
}

const REASON_OPTIONS: DisputeReason[] = [
  'MISSING_STAMP',
  'MISSING_SIGNATURE',
  'WRONG_OUTLET_NAME',
  'WRONG_ITEMS',
  'ILLEGIBLE',
  'DAMAGED',
  'OTHER',
];

const DisputeModal: React.FC<DisputeModalProps> = ({
  isOpen,
  invoice,
  onClose,
  onUpdated,
}) => {
  const [reason, setReason] = useState<DisputeReason>('MISSING_STAMP');
  const [note, setNote] = useState('');
  const [autoFilledNote, setAutoFilledNote] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      const initialReason: DisputeReason = 'MISSING_STAMP';
      const initialNote = DISPUTE_REASON_LABELS[initialReason];
      setReason(initialReason);
      setNote(initialNote);
      setAutoFilledNote(initialNote);
      setIsSubmitting(false);
    }
  }, [isOpen]);

  const handleReasonClick = (opt: DisputeReason) => {
    setReason(opt);
    const label = DISPUTE_REASON_LABELS[opt];
    if (note.trim() === '' || note === autoFilledNote) {
      setNote(label);
      setAutoFilledNote(label);
    }
  };

  const handleNoteChange = (value: string) => {
    setNote(value);
    if (value !== autoFilledNote) {
      setAutoFilledNote(null);
    }
  };

  if (!isOpen || !invoice) return null;

  const canSubmit = note.trim().length > 0 && !isSubmitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setIsSubmitting(true);
    try {
      const updated = await invoicesAPI.dispute(invoice.id, reason, note.trim());
      toast.success('Invoice disputed', {
        description: `${updated.invoiceNumber} moved to disputed.`,
      });
      onUpdated(updated);
      onClose();
    } catch (err) {
      toast.error('Could not dispute invoice', {
        description: err instanceof Error ? err.message : 'Unknown error',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <FormModal
      open={isOpen}
      onClose={onClose}
      icon={<i className="ri-error-warning-line" />}
      iconTone="danger"
      title="Dispute from client"
      subtitle={`${invoice.invoiceNumber} · ${invoice.client.name}`}
      size="lg"
      footer={
        <div className="flex items-center justify-end gap-2">
          <Button tone="neutral" style="stroke" onClick={onClose}>
            Cancel
          </Button>
          <Button
            tone="destructive"
            onClick={handleSubmit}
            disabled={!canSubmit}
            loading={isSubmitting}
          >
            {isSubmitting ? 'Marking disputed…' : 'Mark disputed'}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <div>
          <label className="mb-2 block text-[10px] font-bold uppercase tracking-[0.18em] text-text-sub-600">
            Dispute reason
          </label>
          <div className="grid grid-cols-2 gap-2">
            {REASON_OPTIONS.map((opt) => {
              const active = opt === reason;
              return (
                <button
                  key={opt}
                  type="button"
                  onClick={() => handleReasonClick(opt)}
                  className={`rounded-lg border px-3 py-2 text-sm font-medium tracking-tight text-left transition-colors ${
                    active
                      ? 'border-accent text-accent'
                      : 'border-stroke-soft-200 text-text-sub-600 hover:bg-bg-weak-50'
                  }`}
                >
                  {DISPUTE_REASON_LABELS[opt]}
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <label
            htmlFor="dispute-note"
            className="mb-2 block text-[10px] font-bold uppercase tracking-[0.18em] text-text-sub-600"
          >
            Note <span className="text-error-base">*</span>
          </label>
          <Textarea
            id="dispute-note"
            rows={4}
            value={note}
            onChange={(e) => handleNoteChange(e.target.value)}
            placeholder="What did the client push back on? Logged verbatim to history."
          />
          <p className="mt-2 text-xs text-text-sub-600">
            History note is stored as
            <code className="mx-1 rounded-md bg-bg-weak-50 px-1 py-0.5 text-[11px]">
              [{reason}] {'{note}'}
            </code>
            — Phase 1.1 will split reason into its own column.
          </p>
        </div>
      </div>
    </FormModal>
  );
};

export default DisputeModal;
